import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { ArrowLeft, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { RootState } from '../store'
import { isAdminUser } from '../utils/admin'
import adminMatchesService, {
  type AdminMatchCreatePayload,
  type AdminMatchListItem,
  type AdminMatchWritePayload,
} from '../services/adminMatchesService'
import {
  dedupeAdminMatches,
  filterAdminMatchesForTab,
  hasAdminMatchKickoffTotals,
} from '../utils/adminMatchGroups'

const PAGE_SIZE = 50

const LEAGUES = [
  'premier-league',
  'laliga',
  'serie-a',
  'liga1',
  'ligue1',
  'bundesliga',
  'champions-league',
  'WorldCup2026',
]

const emptyForm = {
  index: '',
  number: '',
  date: '',
  time: 'TBD',
  teamA: '',
  teamB: '',
  league: 'premier-league',
  score: '',
  winner: '',
  playoff: false,
  playoffUniqueGame: false,
}

type MatchTab = 'upcoming' | 'past'

const AdminMatches: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  const [matches, setMatches] = useState<AdminMatchListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [upcomingTotal, setUpcomingTotal] = useState(0)
  const [pastTotal, setPastTotal] = useState(0)
  const [activeTab, setActiveTab] = useState<MatchTab>('upcoming')
  const [search, setSearch] = useState('')
  const [league, setLeague] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<AdminMatchListItem | null>(null)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isAdminUser(user)) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  const filterParams = useMemo(
    () => ({
      search: search.trim() || undefined,
      league: league || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [search, league, dateFrom, dateTo],
  )

  const listParams = useMemo(
    () => ({
      ...filterParams,
      futureOnly: activeTab === 'upcoming' ? true : undefined,
      playedOnly: activeTab === 'past' ? true : undefined,
    }),
    [filterParams, activeTab],
  )

  const syncTabTotals = useCallback(async () => {
    if (!isAdminUser(user)) return
    try {
      const [upRes, pastRes] = await Promise.all([
        adminMatchesService.list({ ...filterParams, futureOnly: true, limit: 1, page: 1 }),
        adminMatchesService.list({ ...filterParams, playedOnly: true, limit: 1, page: 1 }),
      ])
      setUpcomingTotal(upRes.upcomingTotal ?? upRes.total ?? 0)
      setPastTotal(pastRes.pastTotal ?? pastRes.total ?? 0)
    } catch {
      // keep previous totals
    }
  }, [user, filterParams])

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      if (!isAdminUser(user)) return
      if (append) setLoadingMore(true)
      else setLoading(true)
      try {
        const requests: Promise<unknown>[] = [
          adminMatchesService.list({ ...listParams, page: pageNum, limit: PAGE_SIZE }),
        ]
        if (!append) requests.push(syncTabTotals())

        const [res] = (await Promise.all(requests)) as [Awaited<ReturnType<typeof adminMatchesService.list>>]
        const batch = res.matches ?? []
        setMatches((prev) => (append ? dedupeAdminMatches([...prev, ...batch]) : dedupeAdminMatches(batch)))
        if (hasAdminMatchKickoffTotals(res)) {
          setUpcomingTotal(res.upcomingTotal!)
          setPastTotal(res.pastTotal!)
        }
        setPage(pageNum)
        setHasMore(pageNum < (res.totalPages ?? 1))
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Failed to load matches')
        if (!append) {
          setMatches([])
          setUpcomingTotal(0)
          setPastTotal(0)
          setHasMore(false)
        }
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [user, listParams, syncTabTotals],
  )

  const load = useCallback(() => fetchPage(1, false), [fetchPage])

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return
    void fetchPage(page + 1, true)
  }, [fetchPage, page, hasMore, loadingMore])

  useEffect(() => {
    void load()
  }, [load])

  const grandTotal = upcomingTotal + pastTotal
  const activeItems = useMemo(
    () => filterAdminMatchesForTab(matches, activeTab),
    [matches, activeTab],
  )
  const tabs: { id: MatchTab; label: string; count: number }[] = [
    { id: 'upcoming', label: 'Upcoming', count: upcomingTotal },
    { id: 'past', label: 'Past', count: pastTotal },
  ]

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (m: AdminMatchListItem) => {
    setEditing(m)
    const dateStr = typeof m.date === 'string' ? m.date.slice(0, 10) : ''
    setForm({
      index: m.index ?? '',
      number: m.number ?? '',
      date: dateStr,
      time: m.time || 'TBD',
      teamA: m.teamA ?? '',
      teamB: m.teamB ?? '',
      league: m.league ?? 'premier-league',
      score: m.score ?? '',
      winner: m.winner ?? '',
      playoff: m.playoff ?? false,
      playoffUniqueGame: m.playoffUniqueGame ?? false,
    })
    setModalOpen(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (editing?.id) {
        const payload: AdminMatchWritePayload = {
          index: form.index || undefined,
          number: form.number || null,
          date: form.date,
          time: form.time,
          teamA: form.teamA,
          teamB: form.teamB,
          league: form.league,
          score: form.score || null,
          winner: form.winner || null,
          playoff: form.playoff,
          playoffUniqueGame: form.playoffUniqueGame,
        }
        await adminMatchesService.update(editing.id, payload)
        toast.success('Match updated')
      } else {
        if (!form.index.trim()) {
          toast.error('Index is required')
          setSaving(false)
          return
        }
        const payload: AdminMatchCreatePayload = {
          index: form.index.trim(),
          number: form.number || null,
          date: form.date,
          time: form.time,
          teamA: form.teamA,
          teamB: form.teamB,
          league: form.league,
          score: form.score || null,
          winner: form.winner || null,
          playoff: form.playoff,
          playoffUniqueGame: form.playoffUniqueGame,
        }
        await adminMatchesService.create(payload)
        toast.success('Match created')
      }
      setModalOpen(false)
      await load()
    } catch (err: any) {
      const msg = err?.response?.data?.message
      toast.error(Array.isArray(msg) ? msg.join(', ') : msg || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (m: AdminMatchListItem) => {
    if (!m.id) return
    if (!window.confirm(`Delete ${m.teamA} vs ${m.teamB}?`)) return
    try {
      await adminMatchesService.remove(m.id)
      toast.success('Match deleted')
      await load()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Delete failed')
    }
  }

  const renderMatchRow = (m: AdminMatchListItem) => (
    <tr key={m.id} className="border-t border-border hover:bg-muted/20">
      <td className="px-3 py-2 whitespace-nowrap">
        {typeof m.date === 'string' ? m.date.slice(0, 10) : ''}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">{m.time}</td>
      <td className="px-3 py-2 whitespace-nowrap">{m.number ?? '—'}</td>
      <td className="px-3 py-2">{m.teamA}</td>
      <td className="px-3 py-2">{m.teamB}</td>
      <td className="px-3 py-2 whitespace-nowrap">{m.league}</td>
      <td className="px-3 py-2 whitespace-nowrap">{m.score || '—'}</td>
      <td className="px-3 py-2 whitespace-nowrap text-right">
        <button
          type="button"
          onClick={() => openEdit(m)}
          className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"
          title="Edit"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => void handleDelete(m)}
          className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  )

  const renderMatchTable = (items: AdminMatchListItem[], emptyLabel: string) => (
    <div className="rounded-2xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Date</th>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 font-medium">Day</th>
              <th className="px-3 py-2 font-medium">Home</th>
              <th className="px-3 py-2 font-medium">Away</th>
              <th className="px-3 py-2 font-medium">League</th>
              <th className="px-3 py-2 font-medium">Score</th>
              <th className="px-3 py-2 font-medium" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  {emptyLabel}
                </td>
              </tr>
            ) : (
              items.map(renderMatchRow)
            )}
          </tbody>
        </table>
      </div>
    </div>
  )

  if (!isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 max-w-6xl">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="p-2 rounded-xl hover:bg-muted/40 text-muted-foreground">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-foreground">Manage Matches</h1>
                <p className="text-sm text-muted-foreground">
                  {grandTotal} fixtures · {upcomingTotal} upcoming · {pastTotal} past
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold"
            >
              <Plus className="w-4 h-4" /> Create match
            </button>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/20 text-foreground hover:bg-muted/40'
                }`}
              >
                {tab.label}
                <span className="ml-1.5 opacity-80">({tab.count})</span>
              </button>
            ))}
          </div>

          <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <input
              className="px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
              placeholder="Search teams / index…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
              value={league}
              onChange={(e) => setLeague(e.target.value)}
            >
              <option value="">All leagues</option>
              {LEAGUES.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
            <input
              type="date"
              className="px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              type="date"
              className="px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted/40"
            >
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="py-16 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
              Loading…
            </div>
          ) : activeItems.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground rounded-2xl border border-border">
              {activeTab === 'upcoming' ? 'No upcoming matches' : 'No past matches'}
            </div>
          ) : (
            <>
              {renderMatchTable(
                activeItems,
                activeTab === 'upcoming' ? 'No upcoming matches' : 'No past matches',
              )}
              {hasMore && (
                <div className="mt-4 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-6 py-2.5 rounded-xl border border-border bg-muted/30 text-sm font-medium hover:bg-muted/40 disabled:opacity-50 flex items-center gap-2"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading…
                      </>
                    ) : (
                      `Load more (${activeItems.length} of ${activeTab === 'upcoming' ? upcomingTotal : pastTotal})`
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="w-full max-w-lg glass-strong rounded-2xl p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-display font-bold text-foreground">
                {editing ? 'Edit match' : 'Create match'}
              </h2>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Index</label>
                <input
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  value={form.index}
                  onChange={(e) => setForm((f) => ({ ...f, index: e.target.value }))}
                  required={!editing}
                  placeholder="matchIndexPremierLeague2627-10"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Number</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.number}
                    onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                    placeholder="Day 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">League</label>
                  <select
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.league}
                    onChange={(e) => setForm((f) => ({ ...f, league: e.target.value }))}
                  >
                    {LEAGUES.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.time}
                    onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                    placeholder="15:30 or TBD"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Home (teamA)</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.teamA}
                    onChange={(e) => setForm((f) => ({ ...f, teamA: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Away (teamB)</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.teamB}
                    onChange={(e) => setForm((f) => ({ ...f, teamB: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Score</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.score}
                    onChange={(e) => setForm((f) => ({ ...f, score: e.target.value }))}
                    placeholder="1-0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Winner</label>
                  <input
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                    value={form.winner}
                    onChange={(e) => setForm((f) => ({ ...f, winner: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.playoff}
                    onChange={(e) => setForm((f) => ({ ...f, playoff: e.target.checked }))}
                  />
                  Playoff
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.playoffUniqueGame}
                    onChange={(e) => setForm((f) => ({ ...f, playoffUniqueGame: e.target.checked }))}
                  />
                  Unique game
                </label>
              </div>
              <button
                type="submit"
                disabled={saving}
                className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60"
              >
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminMatches
